import { signal, effect } from '@preact/signals-react';
import { ENERGY_GATE_THRESHOLD_DB_DEFAULT } from '../constants.ts';

// Default values
export const defaultValues = {
  threshold: -50,
  interval: 100,
  timeout: 5000,
  disableTimeout: false,
  microphoneDeviceId: 'default',
  // VAD settings
  vadThreshold: 0.7,
  silenceThreshold: 800,
  smartTurnEnabled: false,
  // Energy gate (background-voice suppression)
  energyGateEnabled: false,
  energyGateThresholdDb: ENERGY_GATE_THRESHOLD_DB_DEFAULT
};

// LocalStorage key
const STORAGE_KEY = 'audioStore';

// Load initial values from localStorage or use defaults
const loadInitialState = () => {
  try {
    const savedState = localStorage.getItem(STORAGE_KEY);
    if (savedState) {
      return { ...defaultValues, ...JSON.parse(savedState) };
    }
  } catch (error) {
    console.error('Failed to load audio settings from localStorage:', error);
  }
  return defaultValues;
};

const initialState = loadInitialState();

/**
 * The energy gate used to be a local-only setting persisted under `audioStore`
 * (with the threshold stored as `speakerDropDb`). It now lives on the backend
 * user profile, so we read the old payload once here and let the user saga
 * migrate it. `null` when there is nothing worth migrating.
 *
 * The legacy keys are kept in `audioStore` — the persistence effect below
 * writes them back untouched — until the saga confirms the migration reached
 * the backend. Dropping them earlier would lose the setting for good if that
 * request failed and the page was reloaded.
 */
const readLegacyEnergyGateSettings = (): { energyGateEnabled: boolean; energyGateThresholdDb: number } | null => {
  try {
    const savedState = localStorage.getItem(STORAGE_KEY);
    if (!savedState) {
      return null;
    }

    const parsed = JSON.parse(savedState) as { energyGateEnabled?: unknown; speakerDropDb?: unknown };
    const energyGateEnabled = parsed.energyGateEnabled === true;
    const energyGateThresholdDb =
      typeof parsed.speakerDropDb === 'number' ? parsed.speakerDropDb : defaultValues.energyGateThresholdDb;

    // Nothing was customised locally, so there is nothing to migrate.
    if (!energyGateEnabled && energyGateThresholdDb === defaultValues.energyGateThresholdDb) {
      return null;
    }

    return { energyGateEnabled, energyGateThresholdDb };
  } catch {
    return null;
  }
};

export const legacyEnergyGateSettings = readLegacyEnergyGateSettings();

// Mirrors `legacyEnergyGateSettings` in the shape it has on disk, so the
// persistence effect can round-trip it until the migration is confirmed.
let pendingLegacyEnergyGatePayload: { energyGateEnabled: boolean; speakerDropDb: number } | null =
  legacyEnergyGateSettings
    ? {
        energyGateEnabled: legacyEnergyGateSettings.energyGateEnabled,
        speakerDropDb: legacyEnergyGateSettings.energyGateThresholdDb
      }
    : null;

// Define signals with the loaded values
export const threshold = signal<number>(initialState.threshold);
export const interval = signal<number>(initialState.interval);
export const timeout = signal<number>(initialState.timeout);
export const disableTimeout = signal<boolean>(initialState.disableTimeout);
export const microphoneDeviceId = signal<string>(initialState.microphoneDeviceId);

// VAD signals — hydrated from the backend user profile (see userSaga)
export const vadThreshold = signal<number>(initialState.vadThreshold);
export const silenceThreshold = signal<number>(initialState.silenceThreshold);
export const smartTurnEnabled = signal<boolean>(initialState.smartTurnEnabled);

// Energy gate signals — hydrated from the backend user profile (see userSaga)
export const energyGateEnabled = signal<boolean>(defaultValues.energyGateEnabled);
export const energyGateThresholdDb = signal<number>(defaultValues.energyGateThresholdDb);

/**
 * The audio settings every consumer of the VAD server should honour. Call this
 * during render so the caller stays subscribed to the signals it reads.
 *
 * Spread it into the `useWebsocketAudioModule` options rather than copying the
 * individual signals across — hand-copying is how the energy gate ended up
 * wired into scenario chat only.
 */
export const getGlobalVadSettings = () => ({
  vadThreshold: vadThreshold.value,
  silenceThreshold: silenceThreshold.value,
  smartTurnEnabled: smartTurnEnabled.value,
  energyGateEnabled: energyGateEnabled.value,
  energyGateThresholdDb: energyGateThresholdDb.value
});

/**
 * Transient signal to queue a language switch while audio recognition is active.
 * Holds the pending new source/target language codes until recognition ends or silence threshold.
 */
export const pendingLanguageSwitch = signal<{
  newSourceCode: string;
  newTargetCode: string;
  newSourceTwoLetter: string;
} | null>(null);

// Only device/meter settings are local; the VAD and energy-gate settings live
// on the backend user profile. Any not-yet-migrated legacy energy-gate payload
// is written back verbatim so it survives until the migration succeeds.
const persistLocalSettings = () => {
  const currentState = {
    threshold: threshold.value,
    interval: interval.value,
    timeout: timeout.value,
    disableTimeout: disableTimeout.value,
    microphoneDeviceId: microphoneDeviceId.value,
    ...(pendingLegacyEnergyGatePayload ?? {})
  };

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(currentState));
  } catch (error) {
    console.error('Failed to save audio settings to localStorage:', error);
  }
};

/**
 * Drops the legacy energy-gate payload from localStorage. Call this only once
 * the settings are known to be on the user profile — otherwise a failed
 * migration would lose them permanently.
 */
export const clearLegacyEnergyGateSettings = () => {
  if (!pendingLegacyEnergyGatePayload) {
    return;
  }

  pendingLegacyEnergyGatePayload = null;
  persistLocalSettings();
};

// Setup automatic persistence to localStorage whenever any signal changes
effect(persistLocalSettings);

// Reset function to restore defaults
export function reset() {
  threshold.value = defaultValues.threshold;
  interval.value = defaultValues.interval;
  timeout.value = defaultValues.timeout;
  disableTimeout.value = defaultValues.disableTimeout;
  microphoneDeviceId.value = defaultValues.microphoneDeviceId;
}

export function resetVad() {
  // Reset new VAD settings
  vadThreshold.value = defaultValues.vadThreshold;
  silenceThreshold.value = defaultValues.silenceThreshold;
  smartTurnEnabled.value = defaultValues.smartTurnEnabled;
  energyGateEnabled.value = defaultValues.energyGateEnabled;
  energyGateThresholdDb.value = defaultValues.energyGateThresholdDb;
}
